import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { ModalDialogData } from '../../services/carrier-orders-grid/grid-modal-dispatcher.service';
import { LadingInfo, LadingNote, LadingResponse } from '../../work-orders/work-order-details/work-order.interfaces';

@Component({
  selector: 'app-lading-notes-modal',
  templateUrl: './lading-notes-modal.component.html',
  styleUrls: ['./lading-notes-modal.component.scss'],
})
export class LadingNotesModalComponent implements OnInit {
  @ViewChildren('noteTextarea') noteTextareas!: QueryList<ElementRef<HTMLTextAreaElement>>;

  loading = true;
  saving = false;
  notes: LadingNote[] = [];
  carrierName = '';

  private allNotes: LadingNote[] = [];
  private carrierContactUuid = '';
  private shouldRefreshGrid = false;

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<LadingNotesModalComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: ModalDialogData,
  ) {}

  ngOnInit(): void {
    this.loadNotes();
  }

  get dialogTitle(): string {
    return this.data.modalConfig.dialog_title ?? this.data.headerName ?? 'Lading Notes';
  }

  get entryCountLabel(): string {
    const count = this.notes.length;
    return `${count} ${count === 1 ? 'entry' : 'entries'}`;
  }

  get hasCarrierAudience(): boolean {
    return !!this.carrierContactUuid;
  }

  close(): void {
    this.dialogRef.close({ refreshGrid: this.shouldRefreshGrid });
  }

  addNote(): void {
    if (!this.carrierContactUuid) {
      this.toastr.error('Carrier contact is missing for this lading.');
      return;
    }

    this.notes.unshift({
      note: '',
      contact_uuid: this.carrierContactUuid,
    });

    setTimeout(() => {
      const textarea = this.noteTextareas?.first?.nativeElement;
      if (!textarea) return;
      textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      textarea.focus();
    });
  }

  deleteNote(index: number): void {
    const note = this.notes[index];
    if (!note) return;

    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Confirm Deletion', message: 'Delete this note?' },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.notes.splice(index, 1);
    });
  }

  saveNotes(): void {
    if (this.saving) return;
    if (!this.carrierContactUuid) {
      this.toastr.error('Carrier contact is missing for this lading.');
      return;
    }

    const hiddenNotes = this.allNotes.filter((note) => !this.isCarrierNote(note));
    const visibleNotes = this.notes
      .map((note) => this.normalizeNote(note))
      .filter((note) => !!note.lading_note_uuid || note.note.trim().length > 0);

    const payload = {
      lading_uuid: this.data.lading_uuid,
      notes: [...hiddenNotes, ...visibleNotes].map((note) => ({
        note: note.note,
        lading_note_uuid: note.lading_note_uuid || undefined,
        contact_uuid: note.lading_note_uuid ? (note.contact_uuid || this.carrierContactUuid) : this.carrierContactUuid,
      })),
    };

    this.saving = true;

    this.http.post<any>('/api/work_orders/details/process_lading_notes.php', payload).subscribe({
      next: (response) => {
        this.saving = false;

        if (!response?.success) {
          this.toastr.error(response?.message || 'Error saving notes.');
          return;
        }

        this.allNotes = this.normalizeAndSortNotes(response.lading_notes);
        this.notes = this.extractVisibleNotes(this.allNotes);
        this.shouldRefreshGrid = true;
        this.toastr.success('Notes saved.');
      },
      error: (error) => {
        this.saving = false;
        console.error('Error saving notes:', error);
        this.toastr.error('Error saving notes.');
      },
    });
  }

  noteMeta(note: LadingNote): string {
    const created = this.formatTimestamp(note.date);
    const modified = this.formatTimestamp(note.date_last_modified);

    if (created && modified && modified !== created) {
      return `Created ${created} • Updated ${modified}`;
    }

    if (created) return `Created ${created}`;
    if (modified) return `Updated ${modified}`;
    return 'Unsaved note';
  }

  private loadNotes(): void {
    this.loading = true;

    this.http.get<LadingResponse>(`/api/work_orders/work_order_details.php?uuid=${this.data.lading_uuid}`).subscribe({
      next: (response) => {
        this.configureCarrierContext(response?.lading);
        this.allNotes = this.normalizeAndSortNotes(response?.lading_notes);
        this.notes = this.extractVisibleNotes(this.allNotes);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error loading notes:', error);
        this.toastr.error('Error loading notes.');
      },
    });
  }

  private configureCarrierContext(lading?: LadingInfo): void {
    this.carrierContactUuid = lading?.carrier_contact_uuid || '';
    this.carrierName = lading?.carrier_name || '';
  }

  private normalizeAndSortNotes(notes?: LadingNote[]): LadingNote[] {
    return [...(notes || [])]
      .map((note) => this.normalizeNote(note))
      .sort((a, b) => this.noteSortValue(b) - this.noteSortValue(a));
  }

  private normalizeNote(note: LadingNote): LadingNote {
    return {
      ...note,
      note: note.note ?? '',
      contact_uuid: note.contact_uuid || this.carrierContactUuid || '',
    };
  }

  private extractVisibleNotes(notes: LadingNote[]): LadingNote[] {
    return notes
      .filter((note) => this.isCarrierNote(note))
      .map((note) => ({ ...note }));
  }

  private isCarrierNote(note: LadingNote): boolean {
    const audience = note.contact_uuid || '';
    return !!this.carrierContactUuid && audience === this.carrierContactUuid;
  }

  private noteSortValue(note: LadingNote): number {
    const value = note.date_last_modified || note.date || '';
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
